import { differenceInDays } from "date-fns";
import moment from "moment";
import CryptoJS from "crypto-js";
import i18next from "i18next";

export const convertToBMT = (date: Date | null) => {
  // Convert the input date to GMT (BMT)
  const bmtDate = moment(date)
    .tz(process.env.REACT_APP_DATE_TIMEZONDE ?? "")
    .format("YYYY-MM-DD HH:mm:ss");
  return bmtDate;
};

export const defindTimeZone = (date: any) => {
  return moment(date).tz(process.env.REACT_APP_DATE_TIMEZONDE ?? "");
};

export const isSameDate = (start: Date | string | null, end: Date | string | null) => {
  if (!start || !end) return false;

  const startMoment = moment.utc(start);
  const endMoment = moment.utc(end);

  if (!startMoment.isValid() || !endMoment.isValid()) return false;

  return startMoment.isSame(endMoment, "day");
};

export const calculateDuration = (startDate: Date, endDate: Date, rentalType?: string, rentalDuration?: string | number) => {
  if (!rentalType || rentalType !== 'daily') {
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    const durationInHours = endMoment.diff(startMoment, "hours", true);
    let durationText;
    if (durationInHours >= 8) {
      durationText = "1 day";
    } else {
      durationText = `${durationInHours} hour${durationInHours !== 1 ? "s" : ""}`;
    }

    return durationText;
  } else {
    return rentalDuration + ' ' + ((Number(rentalDuration) > 1) ? i18next.t("days") : i18next.t("day"));
  }

};

export const calculateDurationFlag = (
  startDate: Date | null,
  endDate: Date | null
) => {
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);
  const durationInHours = endMoment.diff(startMoment, "hours");

  let durationText;
  const fullDayHours = Number(process.env.FULL_DAY_HOURS ?? 8);
  if (durationInHours >= fullDayHours) {
    durationText = `daily`;
  } else {
    durationText = `hourly`;
  }

  return durationText;
};

export const countDays = (startDate: Date | null, endDate: Date | null) => {
  if (startDate && endDate) {
    return differenceInDays(endDate, startDate) + 1;
  }
  return 0;
};

export const generateRandomNumber = (min = 1, max = 100) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const encrypt = (data: any, key: any) => {
  return CryptoJS.AES.encrypt(data, key).toString();
};

export const generateEncryptedData = (
  systemToken: string,
  systemKey: string
) => {
  const randomNumber = generateRandomNumber();
  const concatenatedString = `${systemToken}${randomNumber}`;
  const encryptedData = encrypt(concatenatedString, systemKey);

  return {
    randomNumber,
    concatenatedString,
    encryptedData,
  };
};

export const isCookieExpired = (name: any) => {
  const expirationTimestamp = localStorage.getItem(`${name}_expires`);
  if (!expirationTimestamp) return true; // No expiration information

  const currentTimestamp = new Date().getTime();
  return currentTimestamp > parseInt(expirationTimestamp, 10);
};


export const formatPhoneNumber = (phoneNumber: string) => {
  // Check if the number already starts with "+44"
  if (phoneNumber.startsWith("+44")) {
    return phoneNumber;
  }

  // Check if the number starts with "0" and replace it with "+44"
  if (phoneNumber.startsWith("0")) {
    return "+44" + phoneNumber.slice(1);
  }

  // Check if the number starts with "723" or similar and add "+44"
  if (/^\d{9,10}$/.test(phoneNumber)) {
    return "+44" + phoneNumber;
  }

  return phoneNumber;
};

export const formatCountryCodeWithPhoneNumber = (countryCode: string = '+44', phoneNumber: string) => {
  // Check if the number starts with "723" or similar and add "+44"
  // if (/^\d{9,10}$/.test(phoneNumber)) {
  //   return countryCode + phoneNumber;
  // }

  if (phoneNumber.startsWith('0')) {
    return `${countryCode}${phoneNumber.slice(1)}`;
  }

  return countryCode + phoneNumber;
};

export const isInternationalCountryCode = (countryCode: string): boolean => {
  const trimmed = countryCode.trim();

  // Ensure it starts with '+'
  if (!trimmed.startsWith('+')) {
    return false;
  }

  // Check if it's '+44' (UK)
  if (trimmed === '+44') {
    return false;
  }

  // All others are considered international
  return true;
};

export function getReturnDate(
  endDate: string | Date | null,
  holidayDays: string[]
): string {

  if (!endDate) {
    return ''
  }

  // Convert endDate to a Date object
  let end = new Date(endDate);

  // Helper function to get the day name
  function getDayName(date: Date): string {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  }

  // Helper function to get the first working day after a given date
  function getFirstWorkingDayAfter(date: Date): Date {
    let nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);

    // Keep moving forward until we find a non-holiday day
    while (holidayDays.includes(getDayName(nextDate))) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    return nextDate;
  }

  // If the end date is a holiday, return the first working day after it
  if (holidayDays.includes(getDayName(end))) {
    return getFirstWorkingDayAfter(end).toISOString().split('T')[0];
  }

  // If the next day is a holiday, return the first working day after the holiday
  let nextDay = new Date(end);
  nextDay.setDate(nextDay.getDate() + 1);

  if (holidayDays.includes(getDayName(nextDay))) {
    return getFirstWorkingDayAfter(nextDay).toISOString().split('T')[0];
  }

  // If no holidays affect return, return the end date itself
  return new Date(end.toISOString()).toISOString();
}

type StoreSchedule = {
  dayOfWeek: string;
  openTime: string;
  closeTime: string;
  isActive: boolean;
};

export function getInactiveDays(storeSchedule: StoreSchedule[]): string[] {
  return storeSchedule
    .filter((day: any) => !day.isActive) // Filter where isActive = false
    .map((day: any) => day.dayOfWeek); // Extract dayOfWeek
}

export const calculateProductsInCart = (products: any) => {
  let cartQuantity: any = "";

  if (products && products.length > 0) {
    cartQuantity = products.reduce((sum: any, item: any) => sum + item.productQuantity, 0);
  }

  return cartQuantity;
}

export const getSlugFromUrl = (url: string): string | null => {
  const { pathname } = new URL(url);
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] === 'shop' && segments[1]) {
    return segments[1]; // Return the slug
  }

  return null; // No slug found
}